import * as React from 'react'
import { Persona, PersonaSize } from '@fluentui/react'
import { getId } from '@fluentui/utilities'
import { formatFullDate, formatShortDate } from '../helpers/dateHelper'
import { regexDigitsOnly, regexInitials, regexSearchable } from './regexHelper'
import {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListItem,
    ICoreDetailsListSettings,
} from '../components/DetailsList/DetailsListAbstract'
import { Link } from 'react-router-dom'
import { MoneyDto } from '../config/i18nConfig'
import MoneyText from '../components/Text/Currency/MoneyText'
import Tooltip from '../components/Tooltip'

type Settings = Partial<ICoreColumn> & Pick<ICoreColumn, 'key'>

export function fileColumn(settings: Settings, suffix?: string): ICoreColumn {
    const fieldName: string = settings.fieldName || settings.key
    return {
        name: 'Dossiernummer',
        iconName: 'Album',
        isSortable: true,
        minWidth: 60,
        maxWidth: 100,
        isIconOnly: true,
        className: 'ms-textAlignRight',
        headerClassName: 'align-right',
        data: item => regexDigitsOnly(item[fieldName].reference),
        onRender: item =>
            item[fieldName] && (
                <Tooltip hostProps={{ content: `${item[fieldName].reference}: ${item[fieldName].name}` }}>
                    <Link to={`/file/${item[fieldName].id}${suffix}`}>{item[fieldName].reference}</Link>
                </Tooltip>
            ),
        ...settings,
        fieldName,
    }
}

export function moneyColumn<I = ICoreDetailsListItem>(
    settings: Settings,
    getValue?: (item: I) => MoneyDto[],
): ICoreColumn {
    const fieldName: string = settings.fieldName || settings.key
    getValue =
        getValue ||
        ((item: ICoreDetailsListItem): MoneyDto[] =>
            Array.isArray(item[fieldName]) ? item[fieldName] : item[fieldName] ? [item[fieldName]] : [])

    return {
        name: 'Tarief',
        minWidth: 70,
        maxWidth: 100,
        isResizable: true,
        isSortable: true,
        ...(!settings.hasOwnProperty('isIconOnly') && {
            isIconOnly: true,
            iconName: 'Money',
            headerClassName: 'align-right',
        }),
        // The value of the column should be a number so natural sorting can be applied.
        data: (item: I) => (getValue(item).length === 1 ? parseFloat(getValue(item)[0].amount as string) : undefined),
        onRender: (item: I) =>
            getValue(item).map(value => (
                <MoneyText key={getId()} value={value} textProps={{ size: 300 }} align="right" />
            )),
        ...settings,
        fieldName,
    }
}

export function shortDateColumn(settings: Settings): ICoreColumn {
    const fieldName = settings.fieldName || settings.key
    return {
        name: 'Datum',
        iconName: 'Calendar',
        isSortable: true,
        minWidth: 40,
        maxWidth: 40,
        isResizable: false,
        isIconOnly: true,
        className: 'ms-textAlignRight',
        headerClassName: 'align-center',
        onRender: item => (item[fieldName] ? formatShortDate(item[fieldName]) : ''),
        ...settings,
        fieldName,
    }
}

export function localizedDateColumn(settings: Settings): ICoreColumn {
    return {
        ...shortDateColumn(settings),
        minWidth: 68,
        maxWidth: 68,
        onRender: item =>
            item[settings.fieldName] || item[settings.key]
                ? formatFullDate(item[settings.fieldName] || item[settings.key])
                : '',
    }
}

export function personaColumn(settings: Settings): ICoreColumn {
    const fieldName = settings.fieldName || settings.key
    return {
        name: 'Uitvoerder',
        iconName: 'AccountManagement',
        minWidth: 24,
        maxWidth: 24,
        isResizable: false,
        isIconOnly: true,
        isSortable: true,
        isCollapsible: true,
        className: 'persona',
        headerClassName: 'align-center',
        // Get the first letter of each word in order to enable sorting on initials.
        data: item => regexInitials(item[fieldName].fullName),
        onRender: item =>
            item[fieldName] && (
                <Persona
                    key={`persona-${fieldName}`}
                    text={item[fieldName].fullName}
                    size={PersonaSize.size24}
                    hidePersonaDetails
                />
            ),
        ...settings,
        fieldName,
    }
}

export function includeColumnFilter(
    column: Pick<ICoreColumn, 'key'>,
    columnKeys: string[],
    condition: boolean,
): boolean {
    return !columnKeys.includes(column.key) || condition
}

export function multiSelectFilter<I extends Object, S extends ICoreDetailsListSettings>(
    getSetting: (settings: S) => string[],
    getValue: (item: I) => string,
): ICoreDetailsListFilter<I, S> {
    return (items: I[], settings: S): I[] =>
        Array.isArray(getSetting(settings)) && getSetting(settings).length > 0
            ? items.filter((item: I) => getSetting(settings).includes(getValue(item)))
            : items
}

export function searchFilter<I extends Object, S extends ICoreDetailsListSettings>(
    columns: ICoreColumn[],
): ICoreDetailsListFilter<I, S> {
    return (items: I[], settings: S): I[] =>
        settings.query && settings.matchColumns && settings.query.length >= (settings.minQueryLength || 0)
            ? items.filter((item: I) =>
                  (settings.matchColumns || []).some((columnName: string) => {
                      const column = columns.find((column: ICoreColumn) => column.key === columnName)
                      const data = column?.data?.(item) || item[column?.fieldName] || ''
                      return regexSearchable(data.toString()).includes(regexSearchable(settings.query))
                  }),
              )
            : items
}
